/**
 * Skeleton component for calendar page loading state
 * @module components/common/CalendarSkeleton
 */

import React from 'react'
import { Card } from '@/components/ui/Card'
import { SkeletonText, SkeletonRounded, SkeletonCircular } from './Skeleton'

/**
 * Calendar loading skeleton
 *
 * Displays skeleton placeholders matching the structure of the calendar page
 */
export const CalendarSkeleton: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div className="space-y-2">
          <SkeletonText width={280} height={40} />
          <SkeletonText width={380} height={20} />
        </div>
        <SkeletonRounded width={140} height={40} />
      </div>

      {/* Stats Cards */}
      <div className="dashboard-grid lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <SkeletonCircular width={20} height={20} />
            </div>
            <SkeletonText width={60} height={32} className="mb-1" />
            <SkeletonText width={100} height={14} />
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="bg-brand-dark-light rounded-monumenta p-4 border border-brand-gray/10">
        <div className="flex items-center gap-3">
          <SkeletonCircular width={16} height={16} />
          <SkeletonText width={60} height={16} />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonRounded key={index} width={100} height={28} />
            ))}
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="card-hover">
        {/* Calendar Header */}
        <div className="p-6 border-b border-brand-gray/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SkeletonRounded width={60} height={36} />
              <SkeletonRounded width={80} height={36} />
            </div>
            <div className="flex items-center gap-4">
              <SkeletonRounded width={36} height={36} />
              <SkeletonText width={180} height={24} />
              <SkeletonRounded width={36} height={36} />
              <SkeletonRounded width={60} height={32} />
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-6">
          {/* Days of week header */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {Array.from({ length: 7 }).map((_, index) => (
              <div key={index} className="p-2 text-center">
                <SkeletonText width="60%" height={14} className="mx-auto" />
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 35 }).map((_, index) => (
              <div
                key={index}
                className="min-h-[120px] p-2 border border-brand-gray/10 rounded bg-brand-dark-light"
              >
                <SkeletonText width={24} height={16} className="mb-2" />
                <div className="space-y-1">
                  {Math.random() > 0.6 && (
                    <>
                      <SkeletonRounded width="100%" height={24} />
                      {Math.random() > 0.5 && <SkeletonRounded width="100%" height={24} />}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
